import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = "https://awriter-backend-si7g.onrender.com";

const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionLoading, setActionLoading] = useState({});
  const limit = 10;

  useEffect(() => {
    fetchAdmins();
  }, [page]);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/admins?page=${page}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
      });
      setAdmins(response.data.admins);
      setTotalPages(Math.ceil(response.data.total / limit));
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch admins');
      setLoading(false);
      toast.error(err.response?.data?.error || 'Failed to fetch admins');
    }
  };

  const setAdminActionLoading = (adminId, loading) => {
    setActionLoading(prev => ({ ...prev, [adminId]: loading }));
  };

  const handleRoleChange = async (adminId, newRole, currentRole) => {
    if (newRole === currentRole) return; // No change needed

    const action = newRole === 'superadmin' ? 'promote to Superadmin' : 'demote to Admin';
    const confirmMessage = newRole === 'superadmin' 
      ? `Are you sure you want to promote this admin to Superadmin? This will give them full administrative privileges.`
      : `Are you sure you want to demote this superadmin to regular Admin? This will remove their superadmin privileges.`;
    
    if (window.confirm(confirmMessage)) {
      setAdminActionLoading(adminId, true);
      try {
        const endpoint = newRole === 'superadmin' ? 'promote' : 'demote';
        const confirmationField = newRole === 'superadmin' ? 'confirmPromotion' : 'confirmDemotion';
        
        await axios.patch(
          `${API_URL}/api/admin/admins/${adminId}/${endpoint}`,
          { [confirmationField]: true },
          { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
        );
        
        toast.success(`Admin ${action === 'promote to Superadmin' ? 'promoted' : 'demoted'} successfully`);
        fetchAdmins();
      } catch (err) {
        toast.error(err.response?.data?.error || `Failed to ${action.toLowerCase()}`);
        console.error(`Role change error:`, err);
      } finally {
        setAdminActionLoading(adminId, false);
      }
    }
  };

  const handleBlockToggle = async (adminId, isBlocked) => {
    const action = isBlocked ? 'approve (unblock)' : 'block';
    if (window.confirm(`Are you sure you want to ${action} this admin?`)) {
      setAdminActionLoading(adminId, true);
      try {
        await axios.patch(
          `${API_URL}/api/admin/admins/${adminId}`,
          { isBlocked: !isBlocked },
          { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
        );
        toast.success(`Admin ${isBlocked ? 'approved' : 'blocked'} successfully`);
        fetchAdmins();
      } catch (err) {
        toast.error(err.response?.data?.error || `Failed to ${action} admin`);
      } finally {
        setAdminActionLoading(adminId, false);
      }
    }
  };

  const handleDeleteAdmin = async (adminId, adminEmail) => {
    const confirmMessage = `Are you sure you want to DELETE admin "${adminEmail}"? This action cannot be undone.`;
    
    if (window.confirm(confirmMessage)) {
      // Double confirmation for delete action
      const doubleConfirm = window.confirm(`FINAL CONFIRMATION: Delete admin "${adminEmail}" permanently?`);
      
      if (doubleConfirm) {
        setAdminActionLoading(adminId, true);
        try {
          await axios.delete(
            `${API_URL}/api/admin/admins/${adminId}`,
            { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
          );
          toast.success('Admin deleted successfully');
          fetchAdmins();
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to delete admin');
          console.error('Delete admin error:', err);
        } finally {
          setAdminActionLoading(adminId, false);
        }
      }
    }
  };

  const handleActionChange = async (adminId, action, admin) => {
    if (!action || action === '') return;

    switch (action) {
      case 'block':
        await handleBlockToggle(adminId, admin.isBlocked);
        break;
      case 'delete':
        await handleDeleteAdmin(adminId, admin.email);
        break;
      case 'promote':
        await handleRoleChange(adminId, 'superadmin', admin.role);
        break;
      case 'demote':
        await handleRoleChange(adminId, 'admin', admin.role);
        break;
      default:
        break;
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const getAvailableActions = (admin) => {
    const actions = [];
    
    if (admin.role !== 'superadmin') {
      actions.push({
        value: admin.isBlocked ? 'block' : 'block',
        label: admin.isBlocked ? 'Approve' : 'Block',
        disabled: false
      });
      
      actions.push({
        value: 'promote',
        label: 'Promote to Superadmin',
        disabled: false
      });
      
      actions.push({
        value: 'delete',
        label: 'Delete Admin',
        disabled: false,
        isDelete: true
      });
    } else {
      actions.push({
        value: 'demote',
        label: 'Demote to Admin',
        disabled: false
      });
    }
    
    return actions;
  };

  return (
    <div className="p-4 min-h-screen">
      <h1 className="text-2xl font-bold mb-4">Admin Management</h1>
      {error && <p className="text-red-500 mb-4">{error}</p>}
      {loading ? (
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded mb-4"></div>
          <div className="h-64 bg-gray-300 rounded"></div>
        </div>
      ) : admins.length === 0 ? (
        <p className="text-gray-600">No admins found.</p>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {admins.map((admin) => (
                <tr key={admin._id} className={admin.isBlocked ? 'bg-yellow-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{admin.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <select
                      value={admin.role}
                      onChange={(e) => handleRoleChange(admin._id, e.target.value, admin.role)}
                      disabled={actionLoading[admin._id]}
                      className="border border-black rounded px-3 py-1 text-sm bg-white text-black focus:outline-none focus:ring-2 focus:ring-black disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="admin">Admin</option>
                      <option value="superadmin">Superadmin</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {admin.isBlocked ? (
                      <span className="text-yellow-600 font-medium">Awaiting Approval</span>
                    ) : (
                      <span className="text-green-600 font-medium">Active</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(admin.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {admin.lastLoginAt ? new Date(admin.lastLoginAt).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <select
                      onChange={(e) => {
                        const selectedAction = e.target.value;
                        handleActionChange(admin._id, selectedAction, admin);
                        e.target.value = ''; // Reset selection
                      }}
                      disabled={actionLoading[admin._id]}
                      className="border border-black rounded px-3 py-1 text-sm bg-white text-black focus:outline-none focus:ring-2 focus:ring-black disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">Select Action</option>
                      {getAvailableActions(admin).map((action) => (
                        <option 
                          key={action.value} 
                          value={action.value}
                          disabled={action.disabled}
                          className={action.isDelete ? 'text-red-600' : 'text-black'}
                        >
                          {actionLoading[admin._id] ? 'Processing...' : action.label}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-between items-center p-4">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className="px-4 py-2 bg-black text-white rounded disabled:bg-gray-300"
            >
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
              className="px-4 py-2 bg-black text-white rounded disabled:bg-gray-300"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;